import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils.js';

const COMMANDS = [
  { id: 'text', label: 'Text', icon: '📝', type: 'text' },
  { id: 'heading1', label: 'Heading 1', icon: 'H1', type: 'heading1' },
  { id: 'heading2', label: 'Heading 2', icon: 'H2', type: 'heading2' },
  { id: 'heading3', label: 'Heading 3', icon: 'H3', type: 'heading3' },
  { id: 'checklist', label: 'Checklist', icon: '☑️', type: 'checklist' },
  { id: 'quote', label: 'Quote', icon: '💬', type: 'quote' },
  { id: 'code', label: 'Code', icon: '💻', type: 'code' },
  { id: 'url', label: 'URL', icon: '🔗', type: 'url' },
  { id: 'divider', label: 'Divider', icon: '➖', type: 'divider' },
  { id: 'image', label: 'Image', icon: '🖼️', type: 'image' },
  { id: 'video', label: 'Video', icon: '🎥', type: 'video' },
  { id: 'table', label: 'Table', icon: '📊', type: 'table' },
  { id: 'page', label: 'Page', icon: '📄', type: 'page' },
  { id: 'snap', label: 'Snap Ref', icon: '📷', type: 'snap' },
  { id: 'clip', label: 'Clipboard Ref', icon: '📋', type: 'clip' },
];

const SlashMenu = ({ isOpen, onClose, onSelect, position, showAbove }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filter, setFilter] = useState('');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFilter('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const filteredCommands = COMMANDS.filter((cmd) =>
    cmd.label.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredCommands[selectedIndex]) {
        onSelect(filteredCommands[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'Backspace' && filter === '') {
      onClose();
    }
  }, [filteredCommands, selectedIndex, onSelect, onClose, filter]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-[100] bg-zinc-900 border border-zinc-800 shadow-2xl overflow-hidden flex flex-col"
      style={{
        top: position?.top || 0,
        left: position?.left || 0,
        width: 'auto',
        minWidth: '240px',
        maxWidth: '90vw',
        borderRadius: '12px',
        transform: showAbove ? 'translateY(-100%)' : 'none',
      }}
    >
      <div className="p-2 border-b border-zinc-800 shrink-0">
        <input
          ref={inputRef}
          type="text"
          className="w-full bg-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none rounded-lg px-3 py-2"
          placeholder="Filter commands..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div
        className="overflow-y-auto py-1 flex flex-col"
        style={{ maxHeight: '350px' }}
      >
        {filteredCommands.map((cmd, index) => (
          <button
            key={cmd.id}
            className={cn(
              'flex flex-row items-center w-full gap-3 px-3 text-left transition-colors border-none outline-none',
              index === selectedIndex ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-800/50'
            )}
            style={{ 
              height: '44px',
              minHeight: '44px',
              flexShrink: 0,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => onSelect(cmd)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div
              className="flex items-center justify-center shrink-0"
              style={{ 
                width: '24px', 
                height: '24px', 
                fontSize: '16px',
                lineHeight: '1',
                margin: 0,
                padding: 0,
              }}
            >
              {cmd.icon}
            </div>
            <span
              className="text-sm font-medium whitespace-nowrap pr-4"
              style={{
                flex: 1,
              }}
            >
              {cmd.label}
            </span>
          </button>
        ))}
        {filteredCommands.length === 0 && (
          <div className="px-3 py-3 text-sm text-zinc-500 text-center">
            No matching commands
          </div>
        )}
      </div>
    </div>
  );
};

export default SlashMenu;
